import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { Expense, ExpensePaymentMethod } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  datetimeLocalToRecordedAtIso,
  formatLocalYMD,
  toDatetimeLocalValue,
} from './expense-constants'
import { toastHelpers } from '@/lib/toast-helpers'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useExpenseCategoryDefs } from './use-expense-category-defs'
import { useCogsBasis } from './use-cogs-basis'
import { dateOfLocal, manualStockEntryAllowed } from '@/lib/cogsBasis'

const STOCK_HINT = 'Buying stock? Record it in Purchasing (Already bought), or at Day Close if the cash came from the till, so the stock arrives too.'

/**
 * Migration 108 (F-RPT-N35): the fields an accountant needs on every row —
 * who was paid, how, and against which document. All optional; a rent entry
 * may carry none of them. Shared by the Add and Edit dialogs.
 */
type AccountingForm = {
  vendor_id: string
  payee: string
  payment_method: '' | ExpensePaymentMethod
  reference_no: string
  notes: string
}

const EMPTY_ACCOUNTING: AccountingForm = { vendor_id: '', payee: '', payment_method: '', reference_no: '', notes: '' }

const PAYMENT_METHODS: { value: ExpensePaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank', label: 'Bank transfer' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
]

function accountingFromExpense(e: Expense): AccountingForm {
  return {
    vendor_id: e.vendor_id ?? '',
    payee: e.payee ?? '',
    payment_method: e.payment_method ?? '',
    reference_no: e.reference_no ?? '',
    notes: e.notes ?? '',
  }
}

/** Wire shape: "" clears a column on update; undefined would leave it alone,
 *  but the dialogs always send the whole form so the row reflects the screen. */
function accountingPayload(a: AccountingForm) {
  return {
    vendor_id: a.vendor_id,
    payee: a.payee.trim(),
    payment_method: a.payment_method,
    reference_no: a.reference_no.trim(),
    notes: a.notes.trim(),
  }
}

function AccountingFields({ value, onChange }: { value: AccountingForm; onChange: (v: AccountingForm) => void }) {
  // Suppliers come from the purchasing module (admin + manager route). A
  // vendor picked here shows on the ledger and the export by name.
  const { data: suppliersRes } = useQuery({
    queryKey: ['adminSupplierOptions'],
    queryFn: () => apiClient.listSuppliersViaAdmin(),
    staleTime: 5 * 60_000,
  })
  const suppliers = suppliersRes?.data ?? []
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label>Vendor</Label>
          <Select
            value={value.vendor_id || '__none__'}
            onValueChange={v => onChange({ ...value, vendor_id: v === '__none__' ? '' : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="No vendor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No vendor record</SelectItem>
              {suppliers.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Paid to (if no vendor record)</Label>
          <Input
            value={value.payee}
            onChange={e => onChange({ ...value, payee: e.target.value })}
            placeholder="e.g. K-Electric, landlord"
            maxLength={160}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label>Payment method</Label>
          <Select
            value={value.payment_method || '__none__'}
            onValueChange={v => onChange({ ...value, payment_method: v === '__none__' ? '' : (v as ExpensePaymentMethod) })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Not recorded" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Not recorded</SelectItem>
              {PAYMENT_METHODS.map(m => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Invoice / receipt no.</Label>
          <Input
            value={value.reference_no}
            onChange={e => onChange({ ...value, reference_no: e.target.value })}
            placeholder="Vendor's bill number"
            maxLength={120}
          />
        </div>
      </div>
      <div className="grid gap-2">
        <Label>Notes</Label>
        <Textarea
          value={value.notes}
          onChange={e => onChange({ ...value, notes: e.target.value })}
          placeholder="Anything the accountant should know about this entry"
          rows={2}
        />
      </div>
    </>
  )
}

function invalidateExpenseQueries(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['expenses'] })
  qc.invalidateQueries({ queryKey: ['currentDayStatus'] })
  qc.invalidateQueries({ queryKey: ['pnlReport'] })
  qc.invalidateQueries({ queryKey: ['expenseSummary'] })
  qc.invalidateQueries({ queryKey: ['dailyClosings'] })
  qc.invalidateQueries({ queryKey: ['expenseIntelligence'] })
  qc.invalidateQueries({ queryKey: ['expenseCategoryDefs'] })
}

export function AddExpenseDialog({
  open,
  onOpenChange,
  onManageCategories,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  onManageCategories?: () => void
}) {
  const qc = useQueryClient()
  const { currencyCode } = useCurrency()
  const { data: defs = [] } = useExpenseCategoryDefs()

  // F-CTRL-N75: stock categories are not offered — a stock buy is recorded in
  // Purchasing or at Day Close, where the stock arrives with the money.
  // F-RPT-N42: except on a purchases-basis day, where a row under a stock
  // category IS that day's cost of goods — so the owner's own stock categories
  // (meat, drinks) stay available there. "Stock purchase" itself keeps its
  // Day Close / Purchasing paths.
  const offered = useMemo(
    () => defs.filter(d => d.is_active && d.slug !== 'inventory_purchase').sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label)),
    [defs]
  )

  const [form, setForm] = useState({
    category: 'other',
    amount: '',
    description: '',
    recorded_at_local: '',
  })
  const { data: basisState } = useCogsBasis(open)
  const stockOk = manualStockEntryAllowed(basisState?.periods, dateOfLocal(form.recorded_at_local))
  const selectable = useMemo(() => offered.filter(d => !d.is_inventory || stockOk), [offered, stockOk])
  const [accounting, setAccounting] = useState<AccountingForm>(EMPTY_ACCOUNTING)

  useEffect(() => {
    if (!open) return
    const defaultSlug = selectable.some(s => s.slug === 'other')
      ? 'other'
      : (selectable[0]?.slug ?? 'other')
    setForm({
      category: defaultSlug,
      amount: '',
      description: '',
      recorded_at_local: toDatetimeLocalValue(undefined, formatLocalYMD(new Date())),
    })
    setAccounting(EMPTY_ACCOUNTING)
  }, [open, selectable])

  const mut = useMutation({
    mutationFn: () =>
      apiClient.createExpense({
        category: form.category,
        amount: parseFloat(form.amount),
        description: form.description || undefined,
        recorded_at: datetimeLocalToRecordedAtIso(form.recorded_at_local),
        ...accountingPayload(accounting),
      }),
    onSuccess: () => {
      invalidateExpenseQueries(qc)
      toastHelpers.success('Expense added')
      onOpenChange(false)
    },
    onError: (err: Error) => toastHelpers.apiError('Add expense', err),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add expense</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Category</Label>
              {onManageCategories && (
                <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={onManageCategories}>
                  Manage categories
                </Button>
              )}
            </div>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger>
                <SelectValue placeholder={selectable.length ? 'Select category' : 'Loading categories…'} />
              </SelectTrigger>
              <SelectContent>
                {selectable.map(c => (
                  <SelectItem key={c.slug} value={c.slug}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">{STOCK_HINT}</p>
          </div>
          <div className="grid gap-2">
            <Label>Amount ({currencyCode})</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label>Description</Label>
            <Input
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Monthly electricity"
            />
          </div>
          <div className="grid gap-2">
            <Label>Date &amp; time</Label>
            <DateTimePicker
              value={form.recorded_at_local}
              onChange={v => setForm({ ...form, recorded_at_local: v })}
            />
          </div>
          <AccountingFields value={accounting} onChange={setAccounting} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!form.amount || parseFloat(form.amount) <= 0 || mut.isPending || !selectable.length} onClick={() => mut.mutate()}>
            {mut.isPending ? 'Saving…' : 'Add expense'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function EditExpenseDialog({
  expense,
  onClose,
  onManageCategories,
}: {
  expense: Expense | null
  onClose: () => void
  onManageCategories?: () => void
}) {
  const qc = useQueryClient()
  const { currencyCode } = useCurrency()
  const { data: defs = [] } = useExpenseCategoryDefs()
  // F-CTRL-N75: no moving a row INTO a stock category; a row already filed
  // under one keeps it so it stays editable (the server allows the same).
  // F-RPT-N42: on a purchases-basis day the move is allowed (the row becomes
  // that day's cost of goods), decided on the date the row will carry.
  const offered = useMemo(
    () => defs.filter(d => d.is_active && d.slug !== 'inventory_purchase').sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label)),
    [defs]
  )

  const [form, setForm] = useState({
    category: 'other',
    amount: '',
    description: '',
    recorded_at_local: '',
  })
  const { data: basisState } = useCogsBasis(!!expense)
  const stockOk = manualStockEntryAllowed(basisState?.periods, dateOfLocal(form.recorded_at_local) || (expense?.expense_date ?? ''))
  const selectable = useMemo(
    () => offered.filter(d => !d.is_inventory || d.slug === expense?.category || stockOk),
    [offered, expense?.category, stockOk]
  )

  const [accounting, setAccounting] = useState<AccountingForm>(EMPTY_ACCOUNTING)

  useEffect(() => {
    if (!expense) return
    setForm({
      category: expense.category,
      amount: String(expense.amount),
      description: expense.description || '',
      recorded_at_local: toDatetimeLocalValue(expense.recorded_at, expense.expense_date),
    })
    setAccounting(accountingFromExpense(expense))
  }, [expense])

  const mut = useMutation({
    mutationFn: () => {
      if (!expense) return Promise.reject(new Error('No expense'))
      return apiClient.updateExpense(expense.id, {
        category: form.category,
        amount: parseFloat(form.amount),
        description: form.description || undefined,
        recorded_at: datetimeLocalToRecordedAtIso(form.recorded_at_local),
        ...accountingPayload(accounting),
      })
    },
    onSuccess: () => {
      invalidateExpenseQueries(qc)
      toastHelpers.success('Expense updated')
      onClose()
    },
    onError: (err: Error) => toastHelpers.apiError('Update expense', err),
  })

  return (
    <Dialog open={!!expense} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit expense</DialogTitle>
        </DialogHeader>
        {expense && (
          <>
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Category</Label>
                  {onManageCategories && (
                    <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={onManageCategories}>
                      Manage categories
                    </Button>
                  )}
                </div>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {selectable.map(c => (
                      <SelectItem key={c.slug} value={c.slug}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Amount ({currencyCode})</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Description</Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label>Date &amp; time</Label>
                <DateTimePicker
                  value={form.recorded_at_local}
                  onChange={v => setForm({ ...form, recorded_at_local: v })}
                />
              </div>
              <AccountingFields value={accounting} onChange={setAccounting} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onClose()}>
                Cancel
              </Button>
              <Button
                disabled={!form.amount || parseFloat(form.amount) <= 0 || mut.isPending || !selectable.length}
                onClick={() => mut.mutate()}
              >
                {mut.isPending ? 'Saving…' : 'Update'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// Legacy CloseDayDialog removed — the close-out workflow now lives on
// the dedicated /admin/day-close page (DayClosePage). The Expense
// Dashboard links to it from the header.
